/**
 * IMAGE CROPPER COMPONENT
 * 
 * Allows users to drag/pan an image to position it within a fixed aspect ratio frame.
 * Used for profile avatars (1:1) and banners (4:1).
 * 
 * Features:
 * - Drag to reposition (mouse and touch support)
 * - Fixed aspect ratio crop area
 * - Auto-scales image to cover the crop area
 * - Exports cropped image as a compressed blob
 * - Visual guides showing the crop boundary
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Check, Move } from 'lucide-react';

interface ImageCropperProps {
  imageFile: File;
  aspectRatio: number; // width / height (e.g., 4 for 4:1 banner, 1 for square avatar)
  onConfirm: (croppedBlob: Blob, previewUrl: string) => void;
  onCancel: () => void;
  maxOutputWidth?: number; // Max width of exported image
  quality?: number; // JPEG quality 0-1
  title?: string;
}

export default function ImageCropper({
  imageFile,
  aspectRatio,
  onConfirm,
  onCancel,
  maxOutputWidth = 1200,
  quality = 0.85,
  title = 'Position Your Image'
}: ImageCropperProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load the image when file changes
  useEffect(() => {
    const url = URL.createObjectURL(imageFile);
    setImageUrl(url);
    
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageSize({ width: img.width, height: img.height });
      setImageLoaded(true);
    };
    img.src = url;
    
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  // Calculate scale and initial position when image loads or container resizes
  useEffect(() => {
    if (!imageLoaded || !containerRef.current) return;
    
    const container = containerRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = containerWidth / aspectRatio;
    
    setContainerSize({ width: containerWidth, height: containerHeight });
    
    // Calculate scale to cover the container (image should fill the frame)
    const scaleX = containerWidth / imageSize.width;
    const scaleY = containerHeight / imageSize.height;
    const newScale = Math.max(scaleX, scaleY);
    
    setScale(newScale);
    
    // Center the image initially
    const scaledWidth = imageSize.width * newScale;
    const scaledHeight = imageSize.height * newScale;
    
    setPosition({
      x: (containerWidth - scaledWidth) / 2,
      y: (containerHeight - scaledHeight) / 2
    });
  }, [imageLoaded, imageSize, aspectRatio]);

  // Constrain position to keep image covering the container
  const constrainPosition = useCallback((x: number, y: number) => {
    const scaledWidth = imageSize.width * scale;
    const scaledHeight = imageSize.height * scale;
    
    // Image must cover the container, so:
    // - Right edge of image must be >= right edge of container
    // - Bottom edge of image must be >= bottom edge of container
    // - Left edge of image must be <= 0
    // - Top edge of image must be <= 0
    
    const minX = containerSize.width - scaledWidth;
    const minY = containerSize.height - scaledHeight;
    const maxX = 0;
    const maxY = 0;
    
    return {
      x: Math.min(maxX, Math.max(minX, x)),
      y: Math.min(maxY, Math.max(minY, y))
    };
  }, [imageSize, scale, containerSize]);

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    setPosition(constrainPosition(newX, newY));
  }, [isDragging, dragStart, constrainPosition]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault(); // Prevent scrolling while dragging
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    setPosition(constrainPosition(newX, newY));
  }, [isDragging, dragStart, constrainPosition]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Crop and export the image
  const handleConfirm = async () => {
    if (!imageRef.current) return;
    
    setIsProcessing(true);
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');
      
      // Calculate output dimensions (respect maxOutputWidth)
      let outputWidth = containerSize.width;
      let outputHeight = containerSize.height;
      
      // Scale up to get better resolution, but cap at maxOutputWidth
      const upscaleFactor = Math.min(maxOutputWidth / containerSize.width, 2);
      outputWidth = Math.round(containerSize.width * upscaleFactor);
      outputHeight = Math.round(containerSize.height * upscaleFactor);
      
      canvas.width = outputWidth;
      canvas.height = outputHeight;
      
      // Calculate source rectangle (what part of original image to draw)
      // Position is where the image top-left is relative to container top-left
      const sourceX = -position.x / scale;
      const sourceY = -position.y / scale;
      const sourceWidth = containerSize.width / scale;
      const sourceHeight = containerSize.height / scale;
      
      // Draw the cropped portion to canvas
      ctx.drawImage(
        imageRef.current,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, outputWidth, outputHeight
      );
      
      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Create a preview URL for immediate display
            const previewUrl = canvas.toDataURL('image/jpeg', quality);
            onConfirm(blob, previewUrl);
          } else {
            console.error('Failed to create blob');
          }
          setIsProcessing(false);
        },
        'image/jpeg',
        quality
      );
    } catch (err) {
      console.error('Error cropping image:', err);
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 font-heading">{title}</h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Cancel"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Instructions */}
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
          <p className="text-sm text-blue-700 flex items-center gap-2">
            <Move className="w-4 h-4" />
            Drag the image to position it within the frame
          </p>
        </div>
        
        {/* Crop Area */}
        <div className="p-4">
          <div 
            ref={containerRef}
            className="relative overflow-hidden rounded-lg bg-gray-100 cursor-move"
            style={{ 
              aspectRatio: aspectRatio,
              touchAction: 'none' // Prevent browser handling of touch
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            {imageLoaded && (
              <img
                src={imageUrl}
                alt="Crop preview"
                className="absolute pointer-events-none select-none"
                style={{
                  width: imageSize.width * scale,
                  height: imageSize.height * scale,
                  transform: `translate(${position.x}px, ${position.y}px)`,
                  maxWidth: 'none'
                }}
                draggable={false}
              />
            )}
            
            {/* Overlay grid to help with positioning */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Rule of thirds grid */}
              <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
              <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
              <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
              <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
              
              {/* Border to show crop edge */}
              <div className="absolute inset-0 border-2 border-white/50 rounded-lg" />
            </div>
            
            {/* Loading state */}
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600" />
              </div>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-colors disabled:opacity-50 font-body"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing || !imageLoaded}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 font-body"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                Processing...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Confirm
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
